/**
 * Cookie / tracking consent.
 *
 * Single source of truth for what the user has agreed to. We persist the
 * choice in localStorage (no cookie needed for the choice itself) and push
 * it to GA4 via `gtag('consent', 'update', ...)`.
 *
 * Categories:
 *  - necessary: always on (auth, CSRF, app state) — not configurable
 *  - analytics: GA4 page_view + events
 *  - ads:       AdSense personalization (always off for kid accounts)
 *
 * The default (set in index.html) is everything DENIED. We only flip a
 * category to granted when the user explicitly opts in.
 */

export type ConsentCategory = "necessary" | "analytics" | "ads";

export interface ConsentChoice {
  necessary: true;
  analytics: boolean;
  ads: boolean;
  /** ISO timestamp of when the user made the choice. */
  decidedAt: string;
  /** Bumped if we change the categories so we can re-prompt. */
  version: number;
}

const STORAGE_KEY = "gtp.cookieConsent.v1";
export const CONSENT_VERSION = 1;

type GtagFn = (...args: unknown[]) => void;
function getGtag(): GtagFn | null {
  if (typeof window === "undefined") return null;
  const g = (window as unknown as { gtag?: GtagFn }).gtag;
  return typeof g === "function" ? g : null;
}

export function getStoredConsent(): ConsentChoice | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ConsentChoice;
    if (parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function hasDecided(): boolean {
  return getStoredConsent() !== null;
}

/**
 * Apply a consent choice: persist it AND push it to gtag immediately.
 * `kidLock` forces ads off regardless of the user's choice (used for
 * student/under-13 accounts).
 */
export function applyConsent(
  choice: Omit<ConsentChoice, "necessary" | "decidedAt" | "version">,
  opts: { persist?: boolean; kidLock?: boolean } = {},
) {
  const { persist = true, kidLock = false } = opts;

  const effective: ConsentChoice = {
    necessary: true,
    analytics: choice.analytics,
    ads: kidLock ? false : choice.ads,
    decidedAt: new Date().toISOString(),
    version: CONSENT_VERSION,
  };

  if (persist && typeof window !== "undefined") {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(effective));
    } catch {
      // Storage may be unavailable (private mode); choice is still applied
      // to the current session via gtag below.
    }
  }

  const gtag = getGtag();
  if (gtag) {
    gtag("consent", "update", {
      analytics_storage: effective.analytics ? "granted" : "denied",
      ad_storage: effective.ads ? "granted" : "denied",
      ad_user_data: effective.ads ? "granted" : "denied",
      ad_personalization: effective.ads ? "granted" : "denied",
    });
  }

  // Notify listeners (banner, settings dialog) in same and other tabs.
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("gtp:consent-changed", { detail: effective }));
  }

  return effective;
}

/**
 * Re-apply whatever the user previously chose. Call once on app boot so
 * the gtag state matches localStorage after a refresh.
 */
export function rehydrateConsent(opts: { kidLock?: boolean } = {}) {
  const stored = getStoredConsent();
  if (!stored) return null;
  return applyConsent(
    { analytics: stored.analytics, ads: stored.ads },
    { persist: false, kidLock: opts.kidLock },
  );
}

export function revokeConsent() {
  if (typeof window !== "undefined") {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }
  const gtag = getGtag();
  if (gtag) {
    gtag("consent", "update", {
      analytics_storage: "denied",
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
    });
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("gtp:consent-changed", { detail: null }));
  }
}

export function openConsentSettings() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("gtp:open-consent-settings"));
}
